<template>
  <UiModal :model-value="visible" :title="form.id ? t('calendar.editor.editTitle') : t('calendar.editor.newTitle')" size="lg" @update:model-value="requestClose">
    <div v-if="loading" class="ed__loading">{{ t('calendar.loading') }}</div>
    <form v-else class="ed" novalidate @submit.prevent="save">
      <div v-if="review" class="ed__review" role="status">
        <strong>✨ {{ t('calendar.ai.reviewTitle') }}</strong>
        <span>{{ t('calendar.ai.reviewText') }}</span>
        <span v-if="missing.length" class="ed__missing">{{ t('calendar.ai.missing', { fields: missing.map(field => t(`calendar.fields.${field}`)).join(', ') }) }}</span>
      </div>

      <UiInput
        ref="titleInput"
        v-model="form.title"
        class="ed__title"
        :class="{ 'ed__need': missing.includes('title') }"
        :placeholder="t('calendar.editor.titlePlaceholder')"
        :invalid="Boolean(errors.title)"
        maxlength="200"
      />
      <p v-if="errors.title" class="ed__error">{{ errors.title }}</p>

      <div class="ed__types" role="radiogroup" :aria-label="t('calendar.fields.type')">
        <button
          v-for="type in EVENT_TYPES"
          :key="type"
          type="button"
          role="radio"
          class="ed__type"
          :class="{ 'ed__type--on': form.type === type }"
          :aria-checked="form.type === type"
          @click="form.type = type"
        >{{ t(`calendar.eventTypes.${type}`) }}</button>
      </div>

      <section class="ed__block">
        <span class="ed__icon" aria-hidden="true">🕘</span>
        <div class="ed__body">
          <div class="ed__when">
            <div class="ed__point">
              <span class="ed__plabel">{{ t('calendar.editor.starts') }}</span>
              <UiInput v-model="form.startDate" type="date" class="ed__date" :class="{ 'ed__need': missing.includes('date') }" :aria-label="t('calendar.fields.startDate')" :invalid="Boolean(errors.startDate)" @update:model-value="onStartDate" />
              <UiInput v-if="!form.allDay" v-model="form.startTime" type="time" step="300" class="ed__time" :class="{ 'ed__need': missing.includes('startTime') }" :aria-label="t('calendar.fields.startTime')" @update:model-value="onStartTime" />
            </div>
            <div class="ed__point">
              <span class="ed__plabel">{{ t('calendar.editor.ends') }}</span>
              <UiInput v-model="form.endDate" type="date" class="ed__date" :min="form.startDate" :aria-label="t('calendar.fields.endDate')" :invalid="Boolean(errors.endDate)" />
              <UiInput v-if="!form.allDay" v-model="form.endTime" type="time" step="300" class="ed__time" :aria-label="t('calendar.fields.endTime')" :invalid="Boolean(errors.endTime)" />
            </div>
          </div>
          <p v-if="errors.startDate || errors.endTime || errors.endDate || errors.startTime" class="ed__error">{{ errors.startDate || errors.startTime || errors.endTime || errors.endDate }}</p>
          <div class="ed__line">
            <UiSwitch v-model="form.allDay" :label="t('calendar.allDay')" />
            <button type="button" class="ed__link" @click="showZone = !showZone">🌐 {{ form.timezone }}</button>
            <span v-if="otherZoneHint" class="ed__hint">{{ otherZoneHint }}</span>
          </div>
          <UiSelect v-if="showZone" v-model="form.timezone" :options="zoneOptions" searchable />
          <RecurrenceField v-model="form.recurrence" :start-date="form.startDate || today" />
          <p v-if="errors.recurrence" class="ed__error">{{ errors.recurrence }}</p>
        </div>
      </section>

      <section class="ed__block">
        <span class="ed__icon" aria-hidden="true">📍</span>
        <div class="ed__body">
          <UiInput v-model="form.location" :placeholder="t('calendar.fields.location')" maxlength="300" />
        </div>
      </section>

      <section v-if="meetingKinds.length" class="ed__block">
        <span class="ed__icon" aria-hidden="true">🎥</span>
        <div class="ed__body">
          <UiSelect v-model="form.meeting.kind" :options="meetingOptions" />
          <UiInput
            v-if="form.meeting.kind === 'external'"
            v-model="form.meeting.url"
            type="url"
            inputmode="url"
            :placeholder="t('calendar.meeting.urlPlaceholder')"
            :invalid="Boolean(errors.meetingUrl)"
          />
          <p v-if="errors.meetingUrl" class="ed__error">{{ errors.meetingUrl }}</p>
          <UiSwitch v-if="form.meeting.kind !== 'none' && form.meeting.kind !== 'in_person'" v-model="form.meeting.waitingRoom" :label="t('calendar.meeting.waitingRoom')" />
        </div>
      </section>

      <section class="ed__block">
        <span class="ed__icon" aria-hidden="true">👥</span>
        <div class="ed__body">
          <div v-if="form.attendees.length" class="ed__people">
            <span v-for="person in form.attendees" :key="person.userId" class="ed__person" :class="{ 'ed__person--busy': busyIds.has(person.userId) }">
              <span class="ed__avatar">{{ initials(person.name) }}</span>
              {{ person.name }}
              <span v-if="busyIds.has(person.userId)" class="ed__busy">{{ t('calendar.editor.busyThen') }}</span>
              <span v-else-if="person.response && person.response !== 'needs_action'" class="ed__resp">{{ t(`calendar.responses.${person.response}`) }}</span>
              <button type="button" class="ed__x" :aria-label="t('calendar.people.remove', { name: person.name })" @click="removeAttendee(person)">×</button>
            </span>
          </div>
          <div v-for="choice in peopleChoices" :key="choice.term" class="ed__choice">
            <span>{{ t('calendar.ai.whichPerson', { term: choice.term }) }}</span>
            <button v-for="option in choice.options" :key="option.userId" type="button" class="ed__option" @click="pickPerson(choice, option)">{{ option.name }}</button>
            <button type="button" class="ed__option ed__option--none" @click="dropChoice(peopleChoices, choice)">{{ t('calendar.ai.none') }}</button>
          </div>
          <PeoplePicker :exclude="form.attendees.map(person => person.userId)" :placeholder="t('calendar.people.invite')" @pick="addAttendee" />

          <div v-if="form.externals.length" class="ed__people">
            <span v-for="guest in form.externals" :key="guest.email" class="ed__person ed__person--external">
              ✉ {{ guest.name ? `${guest.name} · ` : '' }}{{ guest.email }}
              <span v-if="guest.response && guest.response !== 'needs_action'" class="ed__resp">{{ t(`calendar.responses.${guest.response}`) }}</span>
              <button type="button" class="ed__x" :aria-label="t('calendar.people.remove', { name: guest.email })" @click="removeExternal(guest)">×</button>
            </span>
          </div>
          <div class="ed__line">
            <UiInput
              v-model="externalDraft"
              type="email"
              inputmode="email"
              class="ed__grow"
              :placeholder="t('calendar.people.external')"
              :invalid="Boolean(errors.externals)"
              @keydown.enter.prevent="addExternal"
              @keydown="onExternalKey"
              @blur="addExternal"
            />
          </div>
          <p class="ed__hint">{{ t('calendar.people.externalHint') }}</p>
          <p v-if="errors.externals" class="ed__error">{{ errors.externals }}</p>
        </div>
      </section>

      <section class="ed__block">
        <span class="ed__icon" aria-hidden="true">🔗</span>
        <div class="ed__body">
          <div v-for="choice in recordChoices" :key="choice.term" class="ed__choice">
            <span>{{ t('calendar.ai.whichRecord', { term: choice.term }) }}</span>
            <button v-for="option in choice.options" :key="option.type + option.referenceId" type="button" class="ed__option" @click="pickRecord(choice, option)">
              {{ option.label }}<small v-if="option.hint"> · {{ option.hint }}</small>
            </button>
            <button type="button" class="ed__option ed__option--none" @click="dropChoice(recordChoices, choice)">{{ t('calendar.ai.none') }}</button>
          </div>
          <LinksField v-model="form.links" :suggestions="suggestions" @dismiss="dismissSuggestion" />
          <p v-if="errors.links" class="ed__error">{{ errors.links }}</p>
        </div>
      </section>

      <section class="ed__block">
        <span class="ed__icon" aria-hidden="true">🔔</span>
        <div class="ed__body">
          <RemindersField v-model="form.reminders" />
        </div>
      </section>

      <section class="ed__block">
        <span class="ed__icon" aria-hidden="true">🎨</span>
        <div class="ed__body">
          <div class="ed__colors" role="radiogroup" :aria-label="t('calendar.fields.category')">
            <button
              type="button"
              role="radio"
              class="ed__color ed__color--none"
              :aria-checked="!form.category"
              :class="{ 'ed__color--on': !form.category }"
              :title="t('calendar.categories.none')"
              @click="form.category = null"
            />
            <button
              v-for="category in CATEGORIES"
              :key="category.id"
              type="button"
              role="radio"
              class="ed__color"
              :class="{ 'ed__color--on': form.category === category.id }"
              :style="{ '--swatch': category.color }"
              :aria-checked="form.category === category.id"
              :title="t(`calendar.categories.${category.id}`)"
              @click="form.category = category.id"
            />
            <span class="ed__hint">{{ form.category ? t(`calendar.categories.${form.category}`) : t('calendar.categories.none') }}</span>
          </div>
          <div class="ed__grid">
            <UiField :label="t('calendar.fields.calendar')">
              <UiSelect v-model="form.calendarId" :options="calendarOptions" />
            </UiField>
            <UiField :label="t('calendar.fields.showAs')">
              <UiSelect v-model="form.showAs" :options="showAsOptions" />
            </UiField>
          </div>
          <UiSwitch v-model="privateEvent" :label="t('calendar.fields.private')" />
          <p class="ed__hint">{{ t('calendar.editor.privateHint') }}</p>
        </div>
      </section>

      <section class="ed__block">
        <span class="ed__icon" aria-hidden="true">📝</span>
        <div class="ed__body">
          <UiTextarea v-model="form.description" :rows="4" :maxlength="5000" :placeholder="t('calendar.fields.description')" />
        </div>
      </section>
    </form>

    <template #footer>
      <div class="ed__footer">
        <UiButton v-if="form.id && canCancel" variant="ghost" class="ed__danger" @click="$emit('cancel-event', original)">
          {{ t('calendar.actions.cancelEvent') }}
        </UiButton>
        <span class="ed__spacer" />
        <UiButton variant="secondary" @click="requestClose(false)">{{ t('calendar.cancel') }}</UiButton>
        <UiButton :loading="saving" @click="save">{{ t('calendar.save') }}</UiButton>
      </div>
    </template>
  </UiModal>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  UiButton, UiField, UiInput, UiModal, UiSelect, UiSwitch, UiTextarea,
  useConfirm, useToast, errorMessage, fieldErrors, initials,
} from '@general/ui-kit'

import { calendarApi } from '../api'
import { useCalendarSettings } from '../store'
import { useScopeChoice } from '../scope'
import { CATEGORIES, EVENT_TYPES } from '../lib/constants.mjs'
import {
  addDays, atTime, dateIn, daysBetween, isDate, isTime, minutesToTime, timeIn, timeToMinutes,
} from '../lib/time.mjs'
import RecurrenceField from './RecurrenceField.vue'
import RemindersField from './RemindersField.vue'
import PeoplePicker from './PeoplePicker.vue'
import LinksField from './LinksField.vue'

/**
 * The whole event: when, where, who, what records, reminders, repetition,
 * the meeting link. What opens from «More options», from an event's Edit, and
 * from anywhere that calls useCalendarEditor().open(draft) — the mail reader,
 * the AI box, a record's file. Nothing is saved until Save is pressed.
 */
const props = defineProps({
  draft: { type: Object, default: () => ({}) },
})
const emit = defineEmits(['saved', 'close', 'cancel-event'])
const { t } = useI18n()
const toast = useToast()
const { confirm } = useConfirm()
const { ask } = useScopeChoice()
const { settings, load, zone } = useCalendarSettings()

const visible = ref(true)
const loading = ref(false)
const saving = ref(false)
const showZone = ref(false)
const errors = ref({})
const titleInput = ref(null)
const original = ref(null)
const externalDraft = ref('')
const suggestions = ref([])
const peopleChoices = ref([])
const recordChoices = ref([])
const missing = ref([])
const busyIds = ref(new Set())
const today = computed(() => dateIn(new Date(), zone()))

const form = reactive(blank())
let initial = ''

function blank() {
  const now = new Date()
  const zoneName = zone()
  const date = dateIn(now, zoneName)
  const minutes = Math.min(Math.ceil((now.getHours() * 60 + now.getMinutes() + 1) / 30) * 30, 22 * 60 + 30)
  return {
    id: null, occurrence: null, seriesId: null, calendarId: '', title: '', type: 'event', allDay: false,
    startDate: date, startTime: minutesToTime(minutes), endDate: date, endTime: minutesToTime(minutes + 60),
    timezone: zoneName, location: '', description: '', visibility: 'normal', showAs: 'busy', category: null,
    status: 'confirmed', recurrence: null, reminders: [10], attendees: [], externals: [], links: [],
    meeting: { kind: 'none', url: '', waitingRoom: true },
  }
}

const review = computed(() => props.draft?.review === true)
const meetingKinds = computed(() => settings.value?.meetingKinds ?? [])
const meetingOptions = computed(() => [
  { value: 'none', label: t('calendar.meeting.none') },
  ...meetingKinds.value.map(kind => ({ value: kind.id, label: t(kind.labelKey) })),
])
const calendarOptions = computed(() => (settings.value?.calendars ?? [])
  .filter(calendar => calendar.access === 'owner' || calendar.access === 'writer')
  .map(calendar => ({ value: calendar.id, label: calendarName(calendar) })))
const showAsOptions = computed(() => [
  { value: 'busy', label: t('calendar.showAs.busy') },
  { value: 'free', label: t('calendar.showAs.free') },
])
const zoneOptions = computed(() => {
  let zones = []
  try { zones = Intl.supportedValuesOf('timeZone') } catch { zones = [] }
  const list = new Set([form.timezone, zone(), 'Europe/Madrid', 'Atlantic/Canary', 'Europe/London', 'Europe/Paris', 'UTC', ...zones])
  return [...list].map(value => ({ value, label: value }))
})
const privateEvent = computed({
  get: () => form.visibility === 'private',
  set: (value) => { form.visibility = value ? 'private' : 'normal' },
})
const canCancel = computed(() => Boolean(original.value?.canEdit))
const otherZoneHint = computed(() => {
  if (form.allDay || form.timezone === zone() || !isDate(form.startDate) || !isTime(form.startTime)) return ''
  const start = atTime(form.startDate, form.startTime, form.timezone)
  return t('calendar.editor.yourTime', { time: `${dateIn(start, zone())} ${timeIn(start, zone())}`, zone: zone() })
})

function calendarName(calendar) {
  if (calendar.mine) return t('calendar.calendars.mine')
  if (calendar.kind === 'personal') return t('calendar.calendars.of', { name: calendar.ownerName })
  return calendar.name
}

// ── Loading ─────────────────────────────────────────────────────────────────

onMounted(async () => {
  loading.value = true
  try {
    await load()
    if (props.draft?.id) await fromEvent(props.draft.id, props.draft.occurrence)
    else fromDraft(props.draft ?? {})
  } catch (error) {
    toast.error(errorMessage(error, t))
    emit('close', null)
    return
  } finally {
    loading.value = false
  }
  initial = snapshot()
  await nextTick()
  titleInput.value?.$el?.querySelector?.('input')?.focus?.()
})

function fromDraft(draft) {
  Object.assign(form, blank(), { calendarId: settings.value?.calendarId ?? '' })
  for (const key of ['title', 'type', 'description', 'location', 'visibility', 'showAs', 'category', 'recurrence', 'calendarId']) {
    if (draft[key] !== undefined && draft[key] !== null && draft[key] !== '') form[key] = draft[key]
  }
  if (draft.timezone) form.timezone = draft.timezone
  if (draft.allDay !== undefined) form.allDay = Boolean(draft.allDay)
  if (isDate(draft.startDate)) { form.startDate = draft.startDate; form.endDate = isDate(draft.endDate) ? draft.endDate : draft.startDate }
  if (isTime(draft.startTime)) {
    form.startTime = draft.startTime
    const length = Number(draft.durationMinutes) || 60
    form.endTime = isTime(draft.endTime) ? draft.endTime : minutesToTime(Math.min(timeToMinutes(draft.startTime) + length, 1439))
  }
  if (Array.isArray(draft.reminders)) form.reminders = [...draft.reminders]
  form.attendees = (draft.attendees ?? []).map(person => ({ userId: person.userId ?? person.id, name: person.name ?? '', email: person.email ?? '' }))
  form.externals = (draft.externals ?? []).filter(guest => guest?.email).map(guest => ({ email: String(guest.email).toLowerCase(), name: guest.name ?? '' }))
  form.links = (draft.links ?? []).filter(link => !link.suggested).map(link => ({ type: link.type, referenceId: String(link.referenceId), label: link.label ?? '' }))
  suggestions.value = (draft.links ?? []).filter(link => link.suggested)
  peopleChoices.value = draft.choices?.people ?? []
  recordChoices.value = draft.choices?.records ?? []
  missing.value = draft.missing ?? []
  if (draft.meeting?.kind) form.meeting = { kind: draft.meeting.kind, url: draft.meeting.url ?? '', waitingRoom: draft.meeting.waitingRoom !== false }
  form.source = draft.source ?? null
  if (!isTime(draft.startTime) && draft.startTime === '') missing.value = [...new Set([...missing.value, 'startTime'])]
  // What nobody said stays empty, so Save cannot go through with a guess:
  // «next week» must not become today at the next half hour.
  if (missing.value.includes('date')) { form.startDate = ''; form.endDate = '' }
  if (missing.value.includes('startTime') && !form.allDay) { form.startTime = ''; form.endTime = '' }
}

async function fromEvent(id, occurrence) {
  const event = await calendarApi.get(id, occurrence)
  original.value = event
  const eventZone = event.timezone
  Object.assign(form, blank(), {
    id: event.series && !event.seriesId ? event.series.id : event.id,
    occurrence: event.occurrence ?? event.recurrenceDate ?? null,
    seriesId: event.seriesId,
    calendarId: event.calendarId,
    title: event.title,
    type: event.type,
    allDay: event.allDay,
    timezone: eventZone,
    location: event.location,
    description: event.description,
    visibility: event.visibility,
    showAs: event.showAs,
    category: event.category,
    status: event.status,
    recurrence: event.series?.recurrence ?? event.recurrence ?? null,
    reminders: [...(event.reminders ?? [])],
    attendees: event.attendees.map(person => ({ ...person })),
    externals: event.externals.map(guest => ({ ...guest })),
    links: event.links.map(link => ({ type: link.type, referenceId: link.referenceId, label: link.label })),
    meeting: event.meeting
      ? { kind: event.meeting.kind, url: event.meeting.provider === 'external' ? event.meeting.meetingUrl : '', waitingRoom: event.meeting.waitingRoom }
      : { kind: 'none', url: '', waitingRoom: true },
  })
  if (event.allDay) {
    form.startDate = event.startDate
    form.endDate = event.endDate
  } else {
    form.startDate = dateIn(event.startsAt, eventZone)
    form.startTime = timeIn(event.startsAt, eventZone)
    form.endDate = dateIn(event.endsAt, eventZone)
    form.endTime = timeIn(event.endsAt, eventZone)
  }
}

const snapshot = () => JSON.stringify(form)
const dirty = () => Boolean(initial) && snapshot() !== initial

// ── Editing ─────────────────────────────────────────────────────────────────

let previousStart = null
function onStartDate(value) {
  if (!isDate(value)) return
  // The end moves with the start, keeping the length.
  if (previousStart && isDate(form.endDate)) {
    form.endDate = addDays(form.endDate, daysBetween(previousStart, value))
    if (form.endDate < value) form.endDate = value
  } else form.endDate = value
  previousStart = value
  missing.value = missing.value.filter(field => field !== 'date')
}
watch(() => form.startDate, (value, old) => { if (!previousStart) previousStart = old })

let previousTime = null
function onStartTime(value) {
  if (!isTime(value)) return
  const old = previousTime ?? value
  if (isTime(form.endTime)) {
    const length = timeToMinutes(form.endTime) - timeToMinutes(old)
    form.endTime = minutesToTime(Math.min(timeToMinutes(value) + (length > 0 ? length : 60), 1439))
  }
  previousTime = value
  missing.value = missing.value.filter(field => field !== 'startTime')
}
watch(() => form.startTime, (value, old) => { if (!previousTime) previousTime = old })

function addAttendee(person) {
  if (form.attendees.some(entry => entry.userId === person.id)) return
  form.attendees.push({ userId: person.id, name: person.name, email: person.email, response: 'needs_action' })
}
function removeAttendee(person) {
  form.attendees = form.attendees.filter(entry => entry.userId !== person.userId)
}
const EMAIL = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/
function addExternal() {
  const parts = externalDraft.value.split(/[\s,;]+/).map(part => part.trim().toLowerCase()).filter(Boolean)
  const bad = []
  for (const email of parts) {
    if (!EMAIL.test(email)) { bad.push(email); continue }
    if (!form.externals.some(guest => guest.email === email)) form.externals.push({ email, name: '' })
  }
  externalDraft.value = bad.join(', ')
  errors.value = { ...errors.value, externals: bad.length ? t('calendar.errors.email') : '' }
}
function onExternalKey(event) {
  if (event.key === ',' || event.key === ';') { event.preventDefault(); addExternal() }
}
function removeExternal(guest) {
  form.externals = form.externals.filter(entry => entry.email !== guest.email)
}
function pickPerson(choice, option) {
  addAttendee({ id: option.userId, name: option.name, email: option.email })
  dropChoice(peopleChoices, choice)
}
function pickRecord(choice, option) {
  if (!form.links.some(link => link.type === option.type && link.referenceId === option.referenceId)) {
    form.links.push({ type: option.type, referenceId: option.referenceId, label: option.label })
  }
  dropChoice(recordChoices, choice)
}
function dropChoice(list, choice) {
  list.value = list.value.filter(entry => entry !== choice)
}
function dismissSuggestion(link) {
  suggestions.value = suggestions.value.filter(entry => !(entry.type === link.type && entry.referenceId === link.referenceId))
}

// Whether the people invited are free then — only for who may know.
let availabilityTimer = null
watch(() => [form.startDate, form.startTime, form.endTime, form.allDay, form.attendees.map(person => person.userId).join(',')], () => {
  clearTimeout(availabilityTimer)
  availabilityTimer = setTimeout(checkAvailability, 400)
})
async function checkAvailability() {
  const ids = form.attendees.map(person => person.userId)
  if (!ids.length || !settings.value?.can?.availability || form.allDay || !isDate(form.startDate) || !isTime(form.startTime) || !isTime(form.endTime)) {
    busyIds.value = new Set()
    return
  }
  try {
    const start = atTime(form.startDate, form.startTime, form.timezone).getTime()
    const end = atTime(form.endDate || form.startDate, form.endTime, form.timezone).getTime()
    const answer = await calendarApi.availability({ users: ids.join(','), from: form.startDate, to: form.endDate || form.startDate, tz: form.timezone })
    const own = original.value
    busyIds.value = new Set(answer.people
      .filter(person => person.blocks.some((block) => {
        if (block.allDay) return false
        const a = new Date(block.startsAt).getTime()
        const b = new Date(block.endsAt).getTime()
        // This same event is not a conflict with itself.
        if (own && a === new Date(own.startsAt).getTime() && b === new Date(own.endsAt).getTime()) return false
        return a < end && b > start
      }))
      .map(person => person.userId))
  } catch {
    busyIds.value = new Set()
  }
}

// ── Saving ──────────────────────────────────────────────────────────────────

function body() {
  const out = {
    calendarId: form.calendarId || undefined,
    title: form.title.trim(),
    type: form.type,
    allDay: form.allDay,
    startDate: form.startDate,
    endDate: form.endDate || form.startDate,
    timezone: form.timezone,
    location: form.location,
    description: form.description,
    visibility: form.visibility,
    showAs: form.showAs,
    category: form.category,
    recurrence: form.recurrence,
    reminders: form.reminders,
    attendees: form.attendees.map(person => person.userId),
    externals: form.externals.map(guest => ({ email: guest.email, name: guest.name })),
    links: form.links.map(link => ({ type: link.type, referenceId: link.referenceId })),
  }
  if (!form.allDay) { out.startTime = form.startTime; out.endTime = form.endTime }
  const hadMeeting = Boolean(original.value?.meeting)
  if (form.meeting.kind === 'none') { if (hadMeeting) out.meeting = null } else {
    out.meeting = { kind: form.meeting.kind, url: form.meeting.url, waitingRoom: form.meeting.waitingRoom }
  }
  if (!form.id && form.source) out.source = { kind: form.source.kind, ref: form.source.ref }
  return out
}

function validate() {
  const found = {}
  if (!form.title.trim()) found.title = t('calendar.errors.title')
  if (!isDate(form.startDate)) found.startDate = t('calendar.errors.date')
  if (!form.allDay && !isTime(form.startTime)) found.startTime = t('calendar.errors.time')
  if (!form.allDay && isTime(form.startTime) && isTime(form.endTime) && (form.endDate || form.startDate) === form.startDate && form.endTime <= form.startTime) {
    found.endTime = t('calendar.errors.endBeforeStart')
  }
  if (form.meeting.kind === 'external' && !/^https?:\/\/\S+$/i.test(form.meeting.url.trim())) found.meetingUrl = t('calendar.errors.url')
  errors.value = found
  return !Object.keys(found).length
}

async function save() {
  if (externalDraft.value.trim()) addExternal()
  if (!validate()) return
  let scope = null
  const recurring = Boolean(original.value?.series || original.value?.seriesId)
  if (form.id && recurring) {
    visible.value = false
    scope = await ask({ action: 'edit' })
    visible.value = true
    if (!scope) return
  }
  saving.value = true
  try {
    const payload = body()
    let saved
    if (form.id) {
      // «Only this one» must not rewrite the series' repetition.
      if (scope === 'this') delete payload.recurrence
      saved = await calendarApi.update(form.id, payload, { scope: scope ?? 'all', occurrence: form.occurrence })
    } else {
      saved = await calendarApi.create(payload)
    }
    initial = snapshot()
    toast.success(form.id ? t('calendar.toast.saved') : t('calendar.toast.created'))
    emit('saved', saved)
  } catch (error) {
    const fields = fieldErrors(error, t)
    errors.value = {
      ...fields,
      meetingUrl: fields['meeting.url'] || fields['meeting.kind'],
      recurrence: fields['recurrence.until'] || fields['recurrence.count'] || fields['recurrence.interval'] || fields.recurrence,
    }
    toast.error(errorMessage(error, t))
  } finally {
    saving.value = false
  }
}

async function requestClose(open) {
  if (open) return
  if (dirty()) {
    // Asked with the editor out of the way: a confirmation drawn under an open
    // modal is a question nobody can see.
    visible.value = false
    const leave = await confirm({
      title: t('calendar.editor.discardTitle'),
      message: t('calendar.editor.discardText'),
      confirmLabel: t('calendar.editor.discard'),
      danger: true,
    })
    if (!leave) { visible.value = true; return }
  }
  emit('close', null)
}

defineExpose({ requestClose })
</script>

<style scoped>
.ed { display: flex; flex-direction: column; gap: var(--space-3); }
.ed__loading { padding: var(--space-8); text-align: center; color: var(--color-text-soft); }
.ed__review { display: flex; flex-direction: column; gap: 2px; padding: 10px 12px; border-radius: var(--radius); background: var(--color-info-soft); border: 1px solid var(--color-info); font-size: var(--text-sm); }
.ed__missing { color: var(--color-warning); font-weight: 600; }
.ed__title :deep(input) { font-size: var(--text-xl); font-weight: 600; }
.ed__need :deep(input), .ed__need { box-shadow: 0 0 0 2px var(--color-warning); border-radius: var(--radius-sm); }
.ed__types { display: flex; gap: 6px; flex-wrap: wrap; }
.ed__type { border: 1px solid var(--color-border); background: var(--color-surface); color: var(--color-text-soft); border-radius: var(--radius-full); padding: 6px 12px; font-size: var(--text-sm); cursor: pointer; min-height: 36px; }
.ed__type--on { background: var(--color-primary); border-color: var(--color-primary); color: var(--color-text-on-primary); }
.ed__block { display: grid; grid-template-columns: 28px minmax(0, 1fr); gap: 10px; padding-top: var(--space-3); border-top: 1px solid var(--color-border); }
.ed__icon { font-size: 18px; line-height: 38px; text-align: center; }
.ed__body { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.ed__when { display: flex; flex-wrap: wrap; gap: 8px 20px; }
.ed__point { display: flex; align-items: center; gap: 6px; }
.ed__plabel { font-size: var(--text-xs); font-weight: 600; color: var(--color-text-soft); width: 62px; flex: none; }
.ed__date { width: 158px; }
.ed__time { width: 112px; }
.ed__line { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.ed__grow { flex: 1; min-width: 200px; }
.ed__link { border: 0; background: none; color: var(--color-primary); font-size: var(--text-sm); cursor: pointer; padding: 4px 0; }
.ed__hint { font-size: var(--text-xs); color: var(--color-text-soft); margin: 0; }
.ed__error { font-size: var(--text-xs); color: var(--color-danger); margin: 0; }
.ed__people { display: flex; flex-wrap: wrap; gap: 6px; }
.ed__person { display: inline-flex; align-items: center; gap: 6px; padding: 3px 4px 3px 4px; border-radius: var(--radius-full); background: var(--color-surface-2); border: 1px solid var(--color-border); font-size: var(--text-sm); max-width: 100%; }
.ed__person--external { padding-left: 10px; }
.ed__person--busy { border-color: var(--color-warning); background: var(--color-warning-soft); }
.ed__avatar { width: 24px; height: 24px; border-radius: 50%; background: var(--color-gray-200); display: grid; place-items: center; font-size: 10px; font-weight: 600; }
.ed__busy { font-size: var(--text-xs); color: var(--color-warning); font-weight: 600; }
.ed__resp { font-size: var(--text-xs); color: var(--color-text-soft); }
.ed__x { border: 0; background: none; cursor: pointer; width: 26px; height: 26px; border-radius: 50%; color: var(--color-text-soft); font-size: 15px; }
.ed__x:hover { background: var(--color-gray-100); }
.ed__choice { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; font-size: var(--text-sm); padding: 8px; border-radius: var(--radius); background: var(--color-warning-soft); }
.ed__option { border: 1px solid var(--color-border); background: var(--color-surface); border-radius: var(--radius-full); padding: 4px 10px; cursor: pointer; font-size: var(--text-sm); color: var(--color-text); }
.ed__option:hover { border-color: var(--color-primary); }
.ed__option--none { color: var(--color-text-soft); }
.ed__colors { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ed__color { width: 28px; height: 28px; border-radius: 50%; border: 2px solid transparent; background: var(--swatch); cursor: pointer; box-shadow: inset 0 0 0 2px var(--color-surface); }
.ed__color--none { background: var(--color-surface); border-color: var(--color-border); }
.ed__color--on { border-color: var(--color-text); }
.ed__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(180px, 100%), 1fr)); gap: 8px; }
.ed__footer { display: flex; align-items: center; gap: 8px; width: 100%; flex-wrap: wrap; }
.ed__spacer { flex: 1; }
.ed__danger { color: var(--color-danger); }
@media (max-width: 640px) {
  .ed__when { flex-direction: column; gap: 8px; }
  .ed__point { width: 100%; }
  .ed__plabel { width: 58px; }
  .ed__date { flex: 1.35 1 0; width: auto; min-width: 0; }
  .ed__time { flex: 1 1 0; width: auto; min-width: 0; }
  .ed__type { min-height: 40px; }
  .ed__x { width: 34px; height: 34px; }
  .ed__block { grid-template-columns: 22px minmax(0, 1fr); gap: 8px; }
}
</style>
